/* ==========================================================================
   Rook UI Core — Scroll Area Controller
   Web Component: <rk-scroll-area>
   ========================================================================== */

if (!customElements.get('rk-scroll-area')) {
    class RkScrollArea extends HTMLElement {
        constructor() {
            super();
            this.viewport = null;
            this.trackY = null;
            this.thumbY = null;
            this.trackX = null;
            this.thumbX = null;
            this.corner = null;

            this._dragging = null; // 'y' | 'x' | null
            this._dragStart = 0;
            this._scrollStart = 0;
            this._hideTimer = null;

            this._onScroll = this._onScroll.bind(this);
            this._onResize = this._debounce(this._recalculate.bind(this), 100);
            this._onPointerMove = this._onPointerMove.bind(this);
            this._onPointerUp = this._onPointerUp.bind(this);
        }

        connectedCallback() {
            this.viewport = this.querySelector('.rk-scroll-area__viewport');
            this.trackY = this.querySelector('.rk-scroll-area__scrollbar--vertical');
            this.thumbY = this.trackY?.querySelector('.rk-scroll-area__thumb');
            this.trackX = this.querySelector('.rk-scroll-area__scrollbar--horizontal');
            this.thumbX = this.trackX?.querySelector('.rk-scroll-area__thumb');
            this.corner = this.querySelector('.rk-scroll-area__corner');

            if (!this.viewport) return;

            this._bindEvents();
            this._recalculate();

            // ResizeObserver for dynamic content
            this._resizeObserver = new ResizeObserver(() => this._recalculate());
            this._resizeObserver.observe(this.viewport);

            // Also observe the direct content child
            const content = this.viewport.firstElementChild;
            if (content) {
                this._resizeObserver.observe(content);
            }
        }

        disconnectedCallback() {
            this._resizeObserver?.disconnect();
            window.removeEventListener('resize', this._onResize);
            document.removeEventListener('pointermove', this._onPointerMove);
            document.removeEventListener('pointerup', this._onPointerUp);
        }

        /* ------------------------------------------------------------------ */
        /*  Events                                                            */
        /* ------------------------------------------------------------------ */

        _bindEvents() {
            this.viewport.addEventListener('scroll', this._onScroll, { passive: true });
            window.addEventListener('resize', this._onResize);

            // Thumb drag — vertical
            if (this.thumbY) {
                this.thumbY.addEventListener('pointerdown', (e) => this._onPointerDown(e, 'y'));
            }

            // Thumb drag — horizontal
            if (this.thumbX) {
                this.thumbX.addEventListener('pointerdown', (e) => this._onPointerDown(e, 'x'));
            }

            // Track click — vertical (jump to position)
            if (this.trackY) {
                this.trackY.addEventListener('pointerdown', (e) => {
                    if (e.target === this.trackY) this._onTrackClick(e, 'y');
                });
            }

            // Track click — horizontal (jump to position)
            if (this.trackX) {
                this.trackX.addEventListener('pointerdown', (e) => {
                    if (e.target === this.trackX) this._onTrackClick(e, 'x');
                });
            }

            // Visibility mode: hover
            const visibility = this.dataset.visibility || 'auto';
            if (visibility === 'hover') {
                this.addEventListener('pointerenter', () => this._showScrollbars());
                this.addEventListener('pointerleave', () => this._scheduleHide());
            }
        }

        /* ------------------------------------------------------------------ */
        /*  Scroll handler                                                    */
        /* ------------------------------------------------------------------ */

        _onScroll() {
            this._syncThumbs();

            const visibility = this.dataset.visibility || 'auto';
            if (visibility === 'auto') {
                this._showScrollbars();
                this._scheduleHide();
            }
        }

        /* ------------------------------------------------------------------ */
        /*  Thumb calculations                                                */
        /* ------------------------------------------------------------------ */

        _recalculate() {
            if (!this.viewport) return;

            const vp = this.viewport;
            const hasOverflowY = vp.scrollHeight > vp.clientHeight;
            const hasOverflowX = vp.scrollWidth > vp.clientWidth;

            // Toggle scrollbar visibility based on overflow
            if (this.trackY) {
                this.trackY.classList.toggle('rk-scroll-area__scrollbar--hidden', !hasOverflowY);
            }
            if (this.trackX) {
                this.trackX.classList.toggle('rk-scroll-area__scrollbar--hidden', !hasOverflowX);
            }

            // Corner visibility
            if (this.corner) {
                this.corner.classList.toggle('rk-scroll-area__corner--hidden', !(hasOverflowY && hasOverflowX));
            }

            // Calculate thumb sizes
            if (this.thumbY && hasOverflowY) {
                const ratio = vp.clientHeight / vp.scrollHeight;
                const trackHeight = this.trackY.clientHeight;
                const thumbHeight = Math.max(ratio * trackHeight, 24);
                this.thumbY.style.height = `${thumbHeight}px`;
            }

            if (this.thumbX && hasOverflowX) {
                const ratio = vp.clientWidth / vp.scrollWidth;
                const trackWidth = this.trackX.clientWidth;
                const thumbWidth = Math.max(ratio * trackWidth, 24);
                this.thumbX.style.width = `${thumbWidth}px`;
            }

            this._syncThumbs();
        }

        _syncThumbs() {
            if (!this.viewport) return;
            const vp = this.viewport;

            // Vertical thumb position
            if (this.thumbY && this.trackY) {
                const scrollableY = vp.scrollHeight - vp.clientHeight;
                if (scrollableY > 0) {
                    const ratio = vp.scrollTop / scrollableY;
                    const trackHeight = this.trackY.clientHeight;
                    const thumbHeight = this.thumbY.offsetHeight;
                    const maxOffset = trackHeight - thumbHeight;
                    this.thumbY.style.transform = `translateY(${ratio * maxOffset}px)`;
                }
            }

            // Horizontal thumb position
            if (this.thumbX && this.trackX) {
                const scrollableX = vp.scrollWidth - vp.clientWidth;
                if (scrollableX > 0) {
                    const ratio = vp.scrollLeft / scrollableX;
                    const trackWidth = this.trackX.clientWidth;
                    const thumbWidth = this.thumbX.offsetWidth;
                    const maxOffset = trackWidth - thumbWidth;
                    this.thumbX.style.transform = `translateX(${ratio * maxOffset}px)`;
                }
            }
        }

        /* ------------------------------------------------------------------ */
        /*  Thumb drag                                                        */
        /* ------------------------------------------------------------------ */

        _onPointerDown(e, axis) {
            e.preventDefault();
            e.stopPropagation();
            this._dragging = axis;

            if (axis === 'y') {
                this._dragStart = e.clientY;
                this._scrollStart = this.viewport.scrollTop;
                this.thumbY.classList.add('rk-scroll-area__thumb--active');
            } else {
                this._dragStart = e.clientX;
                this._scrollStart = this.viewport.scrollLeft;
                this.thumbX.classList.add('rk-scroll-area__thumb--active');
            }

            document.addEventListener('pointermove', this._onPointerMove);
            document.addEventListener('pointerup', this._onPointerUp);
        }

        _onPointerMove(e) {
            if (!this._dragging || !this.viewport) return;

            const vp = this.viewport;

            if (this._dragging === 'y') {
                const delta = e.clientY - this._dragStart;
                const scrollableY = vp.scrollHeight - vp.clientHeight;
                const trackHeight = this.trackY.clientHeight;
                const thumbHeight = this.thumbY.offsetHeight;
                const scrollRatio = scrollableY / (trackHeight - thumbHeight);
                vp.scrollTop = this._scrollStart + delta * scrollRatio;
            } else {
                const delta = e.clientX - this._dragStart;
                const scrollableX = vp.scrollWidth - vp.clientWidth;
                const trackWidth = this.trackX.clientWidth;
                const thumbWidth = this.thumbX.offsetWidth;
                const scrollRatio = scrollableX / (trackWidth - thumbWidth);
                vp.scrollLeft = this._scrollStart + delta * scrollRatio;
            }
        }

        _onPointerUp() {
            this.thumbY?.classList.remove('rk-scroll-area__thumb--active');
            this.thumbX?.classList.remove('rk-scroll-area__thumb--active');
            this._dragging = null;
            document.removeEventListener('pointermove', this._onPointerMove);
            document.removeEventListener('pointerup', this._onPointerUp);
        }

        /* ------------------------------------------------------------------ */
        /*  Track click (jump to position)                                    */
        /* ------------------------------------------------------------------ */

        _onTrackClick(e, axis) {
            if (!this.viewport) return;
            const vp = this.viewport;

            if (axis === 'y') {
                const trackRect = this.trackY.getBoundingClientRect();
                const clickRatio = (e.clientY - trackRect.top) / trackRect.height;
                vp.scrollTop = clickRatio * (vp.scrollHeight - vp.clientHeight);
            } else {
                const trackRect = this.trackX.getBoundingClientRect();
                const clickRatio = (e.clientX - trackRect.left) / trackRect.width;
                vp.scrollLeft = clickRatio * (vp.scrollWidth - vp.clientWidth);
            }
        }

        /* ------------------------------------------------------------------ */
        /*  Visibility management                                             */
        /* ------------------------------------------------------------------ */

        _showScrollbars() {
            clearTimeout(this._hideTimer);
            this.classList.add('rk-scroll-area--scrolling');
        }

        _scheduleHide() {
            clearTimeout(this._hideTimer);
            this._hideTimer = setTimeout(() => {
                this.classList.remove('rk-scroll-area--scrolling');
            }, 1200);
        }

        /* ------------------------------------------------------------------ */
        /*  Helpers                                                           */
        /* ------------------------------------------------------------------ */

        _debounce(fn, delay) {
            let timer;
            return (...args) => {
                clearTimeout(timer);
                timer = setTimeout(() => fn(...args), delay);
            };
        }
    }

    customElements.define('rk-scroll-area', RkScrollArea);
}
